<script setup lang="ts" generic="T extends { id: string; label: string }">
import { Map as MapIcon, Plus } from '@lucide/vue'

/**
 * Shared "map type" tile row — large rounded preview tiles with a selected ring + a dashed "Custom"
 * tile (Apple/Google map-type pattern). Presentational: the parent owns the card list, the selected
 * predicate, and the click handlers, so the same tiles render in the desktop style panel and the
 * mobile MapToolSheet identically. Showcased in /styleguide.
 */
defineProps<{
  cards: readonly T[]
  isSelected: (card: T) => boolean
  customOpen?: boolean
}>()
const emit = defineEmits<{ select: [card: T]; 'toggle-custom': [] }>()
</script>

<template>
  <div class="flex gap-3 overflow-x-auto">
    <button
      v-for="card in cards"
      :key="card.id"
      type="button"
      class="flex w-24 shrink-0 flex-col items-center gap-1.5 hover:cursor-pointer"
      :aria-label="card.label"
      @click="emit('select', card)"
    >
      <div
        class="flex h-20 w-24 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 via-white to-sky-100 transition-all"
        :class="
          isSelected(card)
            ? 'border-2 border-emerald-500 ring-2 ring-emerald-500/25'
            : 'border-2 border-transparent ring-1 ring-black/10'
        "
      >
        <MapIcon class="h-7 w-7 text-emerald-600/70" />
      </div>
      <span
        class="line-clamp-2 text-center text-xs font-medium leading-tight"
        :class="isSelected(card) ? 'text-emerald-700' : 'text-gray-700'"
        >{{ card.label }}</span
      >
    </button>

    <!-- Add custom style -->
    <button
      type="button"
      class="flex w-24 shrink-0 flex-col items-center gap-1.5 hover:cursor-pointer"
      aria-label="Add custom style"
      @click="emit('toggle-custom')"
    >
      <div
        class="flex h-20 w-24 items-center justify-center rounded-xl border-2 border-dashed transition-colors"
        :class="
          customOpen ? 'border-emerald-500 text-emerald-600' : 'border-gray-300 text-gray-400'
        "
      >
        <Plus :size="22" />
      </div>
      <span class="text-center text-xs font-medium leading-tight text-gray-700">Custom</span>
    </button>
  </div>
</template>
